import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from "formik";
import { ReactComponent as LeftArrow } from "../../resources/icons/left_arrow.svg";
import CustomSelect from "../elements/CustomSelect";
import successIcon from "../../resources/icons/success.svg";
import Modal from "../elements/Modal";
import { CREATE_USER_VALIDATION } from "../forms/Validations";

const AdminEditUser: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editedUser, setEditedUser] = useState("");

  const navigate = useNavigate();

  const [initialValues, setInitialValues] = useState({
    name: "",
    role: "",
    email: "",
    phone: "",
    password: "",
  });

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token");
      try {
        const res = await fetch(
          `${process.env.REACT_APP_URL}/admin/users?page=1&limit=100`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await res.json();
        const user = data.users.find((u: any) => u.id.toString() === id);
        if (user) {
          setInitialValues({
            name: user.name,
            role: user.type.charAt(0) + user.type.slice(1).toLowerCase(),
            email: user.email,
            phone: user.phone,
            password: "",
          });
        }
      } catch {}
    };

    fetchUser();
  }, [id]);

  const onSubmit = async (
    values: typeof initialValues,
    { setSubmitting }: FormikHelpers<typeof initialValues>
  ) => {
    const token = localStorage.getItem("token");

    const payload: Record<string, any> = {};

    if (values.email !== initialValues.email) payload.email = values.email;
    if (values.name !== initialValues.name) payload.name = values.name;
    if (values.phone !== initialValues.phone) payload.phone = values.phone;
    if (values.role !== initialValues.role)
      payload.type = values.role.toUpperCase().replace(" ", "_");
    if (values.password) payload.password = values.password;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_URL}/admin/users/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        alert(data.error || "Update failed.");
        return;
      }

      setEditedUser(values.name);
      setIsModalOpen(true);
    } catch {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex flex-row flex-1">
        <div className="flex-1 ml-[17.5rem] flex flex-col">
          <div className="flex flex-row pl-6 pt-6  border-b">
            <button
              onClick={() => navigate("/admin/management")}
              className="flex items-center font-bold mb-4"
            >
              <LeftArrow className="w-6 h-6" style={{ color: "#344054" }} />
            </button>
            <h1 className="text-[#344054] font-inter font-bold text-[24px] leading-[100%] tracking-[0%]">
              Edit User
            </h1>
          </div>

          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={CREATE_USER_VALIDATION}
            onSubmit={onSubmit}
          >
            {() => (
              <Form className="w-full flex-1 flex flex-col justify-between">
                <div>
                  <div className="mb-4 w-[23rem] pl-6 mt-4">
                    <label htmlFor="name" className="block">
                      Name
                    </label>
                    <Field
                      type="text"
                      id="name"
                      name="name"
                      placeholder="User name"
                      className="border rounded p-2 w-full text-[#101828] placeholder-[#667085]"
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>

                  <div className="mb-4 w-[23rem] pl-6">
                    <label htmlFor="role" className="block">
                      Role
                    </label>
                    <CustomSelect name="role" />
                    <ErrorMessage
                      name="role"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>

                  <div className="mb-4 w-[23rem] pl-6">
                    <label htmlFor="email" className="block">
                      Email
                    </label>
                    <Field
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Enter user email"
                      className="border rounded p-2 w-full text-[#101828] placeholder-[#667085]"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>

                  <div className="mb-4 w-[23rem] pl-6">
                    <label htmlFor="phone" className="block">
                      Phone Number
                    </label>
                    <Field name="phone">
                      {({ field, form }: any) => (
                        <input
                          {...field}
                          id="phone"
                          type="text"
                          placeholder="Enter user phone number"
                          className="border rounded p-2 w-full text-[#101828] placeholder-[#667085]"
                          onChange={(e) => {
                            const noSpaces = e.target.value.replace(/\s+/g, "");
                            form.setFieldValue("phone", noSpaces);
                          }}
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="phone"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>

                  <div className="mb-4 w-[23rem] pl-6">
                    <label htmlFor="password" className="block">
                      Password
                    </label>
                    <Field
                      type="password"
                      id="password"
                      name="password"
                      placeholder="Set new password"
                      className="border rounded p-2 w-full text-[#101828] placeholder-[#667085]"
                    />
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                </div>

                <div className="flex justify-start gap-4 mt-auto border-t pl-6 py-4">
                  <button
                    type="button"
                    onClick={() => navigate("/admin/management")}
                    className="px-4 py-2 bg-white border rounded-md font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#1672EC] text-white rounded font-semibold"
                  >
                    Save Changes
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <Modal isOpen={isModalOpen} maxWidth="32rem">
        <div className="flex flex-col ">
          <div className="w-12 h-12 bg-[#DCFAE6] rounded-full flex items-center justify-center mx-4 my-4">
            <img src={successIcon} alt="Success" className="w-5 h-5" />
          </div>
          <h2 className="text-xl font-bold  mx-4 my-2">User edited!</h2>
          <p className="text-gray-600 pb-4 mx-4">
            You’ve successfully edited the account of
            <span className="font-semibold"> {editedUser}.</span>
          </p>
          <span className="border-[1px]"></span>
          <button
            onClick={() => navigate("/admin/management")}
            className="px-4 py-2 mx-4 my-6 bg-buttonBlue text-white rounded-md font-semibold"
          >
            Go to User Management
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminEditUser;
